import { subDays } from "date-fns";

import {
  AmpProjectLinkKind,
  type AmpProjectLinkFragment,
  type AmpProjectLinkSourceFragment,
  type AmpProjectLinkTargetInput,
} from "src/graphql";
import {
  type DescribedLink,
  describeLink,
  LINK_KIND_LABELS,
  type LinkKind,
} from "src/pages/projects/links";

/** A link on a task or subtask names it, when it shows on a parent's page. */
export type AttachmentLink = AmpProjectLinkFragment &
  Partial<AmpProjectLinkSourceFragment>;

export const NO_ATTACHMENTS_MESSAGE =
  "Attach ads, emails, web pages, files, links, audiences, or syncs. Briefs and other linked work show up here too.";

/** What an attachment is, then the facts about it, for a line such as "Audience · 48,200 members". */
export function attachmentSummary(
  described: DescribedLink,
  facts: string[],
): string[] {
  return [
    LINK_KIND_LABELS[described.kind],
    // An email's facts lead with its subject, which is also its subtitle.
    ...(described.subtitle && described.kind !== "email"
      ? [described.subtitle]
      : []),
    ...facts,
  ];
}

/** Where an attachment hangs: the project itself, or one of its tasks. */
export const ON_PROJECT = "project";

// Work a project produces, which the Attachments tab shows as previews rather than rows.
const CREATIVE_KINDS: ReadonlySet<LinkKind> = new Set([
  "ad",
  "email",
  "sms",
  "push",
  "document",
  "webPage",
  "canvas",
]);

export type ProjectAttachment = {
  link: AttachmentLink;
  described: DescribedLink;
  /** The Library file to preview, for creative work that is one. */
  fileId: string | null;
  /** The task it's attached through, or `ON_PROJECT`. */
  attachedTo: string;
  /** When it was attached or its file last changed, whichever is later. */
  touchedAt: string;
};

export function toProjectAttachments(
  links: AttachmentLink[],
): ProjectAttachment[] {
  return links.map((link) => {
    const described = describeLink(link);
    const fileUpdatedAt = link.amp_file?.updated_at;
    return {
      link,
      described,
      fileId:
        CREATIVE_KINDS.has(described.kind) && link.amp_file
          ? link.amp_file.id
          : null,
      attachedTo: link.source_task?.id ?? ON_PROJECT,
      touchedAt:
        fileUpdatedAt && toTime(fileUpdatedAt) > toTime(link.created_at)
          ? fileUpdatedAt
          : link.created_at,
    };
  });
}

/** Whether it is a Library file with something to download; a canvas has none. */
export function isDownloadable({ link }: ProjectAttachment): boolean {
  return link.amp_file !== null && link.amp_file.type !== "canvas";
}

/** The Library file behind an uploaded attachment, which previews only when it's an image. */
export function uploadFileId({ link }: ProjectAttachment): string | null {
  return link.amp_file?.type === "upload" ? link.amp_file.id : null;
}

/** Newest first, by when each was attached or its file last changed, as the Attachments tab lists them. */
export function newestFirst(a: ProjectAttachment, b: ProjectAttachment) {
  return toTime(b.touchedAt) - toTime(a.touchedAt);
}

export type AttachmentFilters = {
  search: string;
  /** Null shows every kind. */
  kinds: ReadonlySet<LinkKind> | null;
  /** Null shows attachments on the project and every task. */
  attachedTo: ReadonlySet<string> | null;
};

export function filterAttachments(
  attachments: ProjectAttachment[],
  { search, kinds, attachedTo }: AttachmentFilters,
): ProjectAttachment[] {
  const query = search.trim().toLowerCase();
  return attachments.filter(
    ({ described, attachedTo: target }) =>
      (kinds === null || kinds.has(described.kind)) &&
      (attachedTo === null || attachedTo.has(target)) &&
      (query === "" || described.title.toLowerCase().includes(query)),
  );
}

/** Creative work with a file to preview, and everything else. */
export function splitCreative(attachments: ProjectAttachment[]): {
  creative: ProjectAttachment[];
  other: ProjectAttachment[];
} {
  return {
    creative: attachments.filter(({ fileId }) => fileId !== null),
    other: attachments.filter(({ fileId }) => fileId === null),
  };
}

/** What it takes to attach the same thing again: a record to link, or a web link. */
type AttachmentRelink =
  | { type: "work"; target: AmpProjectLinkTargetInput }
  | { type: "url"; url: string; label: string };

/** Null for an attachment that can't be attached a second time, such as a chat or an agent action. */
export function attachmentRelink(
  link: AttachmentLink,
): AttachmentRelink | null {
  // A Library file attaches by its file id, whatever kind of work it holds.
  if (link.amp_file) {
    return {
      type: "work",
      target: { kind: AmpProjectLinkKind.File, id: link.amp_file.id },
    };
  }
  if (link.audience) {
    return {
      type: "work",
      target: {
        kind: AmpProjectLinkKind.Audience,
        id: String(link.audience.id),
      },
    };
  }
  if (link.sync) {
    return {
      type: "work",
      target: { kind: AmpProjectLinkKind.Sync, id: String(link.sync.id) },
    };
  }
  if (link.marketing_campaign) {
    return {
      type: "work",
      target: {
        kind: AmpProjectLinkKind.Campaign,
        id: link.marketing_campaign.id,
      },
    };
  }
  if (link.campaign_artifact) {
    return {
      type: "work",
      target: {
        kind: AmpProjectLinkKind.CampaignArtifact,
        id: link.campaign_artifact.id,
      },
    };
  }
  if (link.ad_studio_project_brief_version) {
    return {
      type: "work",
      target: {
        kind: AmpProjectLinkKind.BriefVersion,
        id: link.ad_studio_project_brief_version.id,
      },
    };
  }
  if (link.amp_opportunity) {
    return {
      type: "work",
      target: {
        kind: AmpProjectLinkKind.Opportunity,
        id: link.amp_opportunity.id,
      },
    };
  }
  const external = describeLink(link);
  if (external.kind === "external" && external.href) {
    return { type: "url", url: external.href, label: external.title };
  }
  return null;
}

export type AttachmentActor =
  | { kind: "user"; name: string }
  | { kind: "agent" };

/** Who attached it. An agent's attachment has no creating user. */
export function attachmentActor({
  created_by_user,
}: AttachmentLink): AttachmentActor {
  return created_by_user
    ? { kind: "user", name: created_by_user.name }
    : { kind: "agent" };
}

export const ATTACHMENT_GROUPINGS = ["type", "attachedTo", "none"] as const;
type AttachmentGrouping = (typeof ATTACHMENT_GROUPINGS)[number];

export const ATTACHMENT_GROUPING_LABELS: Record<AttachmentGrouping, string> = {
  type: "Type",
  attachedTo: "Attached to",
  none: "No grouping",
};

export const ATTACHMENT_ORDERINGS = ["updated", "name"] as const;
type AttachmentOrdering = (typeof ATTACHMENT_ORDERINGS)[number];

export const ATTACHMENT_ORDERING_LABELS: Record<AttachmentOrdering, string> = {
  updated: "Last updated",
  name: "Name",
};

export type AttachmentDisplay = {
  grouping: AttachmentGrouping;
  ordering: AttachmentOrdering;
};

export const DEFAULT_ATTACHMENT_DISPLAY: AttachmentDisplay = {
  grouping: "type",
  ordering: "updated",
};

type SourceTask = NonNullable<AttachmentLink["source_task"]>;

const ATTACHMENT_TYPE_SECTIONS = ["creative", "other"] as const;
type AttachmentTypeSection = (typeof ATTACHMENT_TYPE_SECTIONS)[number];

export const ATTACHMENT_TYPE_SECTION_LABELS: Record<
  AttachmentTypeSection,
  string
> = {
  creative: "Creative",
  other: "Other attachments",
};

export type AttachmentGroup = {
  key: string;
  attachments: ProjectAttachment[];
} & (
  | { by: "type"; section: AttachmentTypeSection }
  /** A null task is the project itself. */
  | { by: "attachedTo"; task: SourceTask | null }
  | { by: "none" }
);

/** Attachments in the display's order, split into its groups. With no grouping, one group holds them all. */
export function arrangeAttachments(
  attachments: ProjectAttachment[],
  { grouping, ordering }: AttachmentDisplay,
): AttachmentGroup[] {
  const sorted = sortAttachments(attachments, ordering);
  if (grouping === "none") {
    return sorted.length > 0
      ? [{ key: "all", by: "none", attachments: sorted }]
      : [];
  }
  if (grouping === "type") {
    const { creative, other } = splitCreative(sorted);
    const sections: Record<AttachmentTypeSection, ProjectAttachment[]> = {
      creative,
      other,
    };
    return ATTACHMENT_TYPE_SECTIONS.filter(
      (section) => sections[section].length > 0,
    ).map((section) => ({
      key: `type:${section}`,
      by: "type",
      section,
      attachments: sections[section],
    }));
  }
  const byTarget = groupBy(sorted, ({ attachedTo }) => attachedTo);
  const onProject = byTarget.get(ON_PROJECT);
  const onTasks = [...byTarget.values()].flatMap((items) => {
    const task = items[0]?.link.source_task;
    return task ? [{ task, items }] : [];
  });
  // Without a sort, tasks run by their latest change, since the items already run newest first.
  if (ordering === "name") {
    onTasks.sort((a, b) => a.task.title.localeCompare(b.task.title));
  }
  return [
    ...(onProject
      ? [
          {
            key: `attachedTo:${ON_PROJECT}`,
            by: "attachedTo" as const,
            task: null,
            attachments: onProject,
          },
        ]
      : []),
    ...onTasks.map(({ task, items }) => ({
      key: `attachedTo:${task.id}`,
      by: "attachedTo" as const,
      task,
      attachments: items,
    })),
  ];
}

function sortAttachments(
  attachments: ProjectAttachment[],
  ordering: AttachmentOrdering,
): ProjectAttachment[] {
  return [...attachments].sort(
    ordering === "name"
      ? (a, b) =>
          a.described.title.localeCompare(b.described.title) ||
          newestFirst(a, b)
      : newestFirst,
  );
}

function groupBy<K, T>(items: T[], keyOf: (item: T) => K): Map<K, T[]> {
  const groups = new Map<K, T[]>();
  for (const item of items) {
    const key = keyOf(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return groups;
}

/** Attachments added or changed in the last `days`, newest first, up to `limit`. */
export function recentAttachments(
  attachments: ProjectAttachment[],
  {
    days,
    limit,
    now = new Date(),
  }: { days: number; limit: number; now?: Date },
): ProjectAttachment[] {
  const since = subDays(now, days).getTime();
  return attachments
    .filter(({ touchedAt }) => toTime(touchedAt) >= since)
    .sort(newestFirst)
    .slice(0, limit);
}

// Hasura's timestamps carry an offset and microseconds, so they compare as times, not strings.
function toTime(iso: string): number {
  return new Date(iso).getTime();
}
